import { FC, useRef, useState } from 'react';
import classNames from 'classnames';
import { FullViewCarousel } from 'tedooo-web-design-system/ui';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { CarouselRef } from 'antd/lib/carousel';

import { Image } from 'components/helpers';
import TotalSavesTag from 'components/totalSavesTag/TotalSavesTag';
import CircleLikeButton from 'components/circleLikeButton/CircleLikeButton';
import ProductDetails from './productDetails/ProductDetails';
import { SpecificProductSelector } from 'store/specificProduct/SpecificProductSelector';
import { UserSelector } from 'store/user/UserSelector';
import { SettingsSelector } from 'store/settings/SettingsSelector';
import { PopupSlicer } from 'store/popup/PopupActions';
import {
  popupIdsOptions,
  IProductDetailsWrapper,
  BasketPageSources,
  IStore2,
  PayoutMethods,
  NonSignInPopupSources,
} from 'types';
import { BasketSlicer } from 'store/basket/BasketSlicer';
import { parseToCheckoutItem } from './helper';
import { ROUTES } from 'constant';
import { onAddItemToBasket } from 'store/basket/BasketActions';
import ProductPageSkeleton from '../../components/skeletons/productPageSkeleton/ProductPageSkeleton';
import { CheckoutSlicer } from 'store/checkoutForNonSignupUsers/CheckoutSlicer';

const ProductDetailsWrapper: FC<IProductDetailsWrapper> = ({
  token,
  product,
  estimatedDeliveryTime,
  isShopOwner,
  onLikeToggle,
  vacationDetails,
  showPopup,
  isConnectedToPaypal,
  onBuyNowItem,
  bannerDetails,
  shippingCountries,
  recipientCountry,
  onEdit,
  defaultShippingAddress,
  addItemToBasket,
  updateBasketSource,
  updateInitialDetails,
  isLoading,
  shopId,
}) => {
  const [isAddedToBasketClicked, setIsAddedToBasketClicked] = useState(false);

  const [selectedQuantity, setSelectedQuantity] = useState<number>(1);

  const [displayedImageIndex, setDisplayedImageIndex] = useState(0);

  const ref = useRef<CarouselRef | null>(null);

  const navigate = useNavigate();

  const handleImageClick = (index) => {
    setDisplayedImageIndex(index);
    if (ref.current) {
      ref.current.goTo(index);
    }
  };

  const handleCarouselChange = (current) => {
    setDisplayedImageIndex(current);
  };

  const onCountryChange = () => {
    showPopup({ id: popupIdsOptions.selectShippingAddressPopup });
  };

  const onShare = () => {
    showPopup({ id: popupIdsOptions.shareItemPopup, productId: product.id });
  };

  const onBuyNow = (
    customization: { text: string; images: string[] },
    isBuyWithPaypal: boolean,
  ) => {
    const basketItem = parseToCheckoutItem(product, selectedQuantity, customization);

    if (!token) {
      if (!product.hasInstantCheckout) {
        showPopup({
          id: popupIdsOptions.signInPopup,
          productId: product.id,
          source: NonSignInPopupSources.BuyItem,
        });
      } else {
        showPopup({
          id: popupIdsOptions.goToCheckoutPopup,
          source: NonSignInPopupSources.BuyItem,
          checkoutItemDetails: {
            shopId,
            product: basketItem,
            isBuyWithPaypal,
            countries: product.shippingInformation?.countries || [],
          },
        });
      }
    } else if (product.hasInstantCheckout) {
      updateInitialDetails({
        shopId,
        products: [basketItem],
        shippingAddress: defaultShippingAddress,
        checkoutId: null,
        countries: product.shippingInformation?.countries || [],
      });
      navigate(`/${ROUTES.NON_SIGNUP_CHECKOUT}`);
    } else if (recipientCountry || product.isDigital) {
      onBuyNowItem({
        shopId,
        item: basketItem,
        hasInstantCheckout: product.hasInstantCheckout,
        isBuyWithPaypal,
      });
    } else {
      showPopup({ id: popupIdsOptions.selectShippingAddressPopup });
    }
  };

  const onAddToBasket = (customization: { text: string; images: string[] }) => {
    if (isAddedToBasketClicked) {
      updateBasketSource({ source: BasketPageSources.AddedToBasketButton });
      navigate(`/${ROUTES.BASKET}`);
    } else {
      const item = parseToCheckoutItem(product, selectedQuantity, customization);
      addItemToBasket({
        details: {
          id: item.id,
          quantity: item.quantity,
          price: item.price,
          title: item.title,
          image: item.image,
          customizationRequest: item.customizationRequest,
        },
      });
    }
    setIsAddedToBasketClicked(true);
  };
  if (isLoading) {
    return <ProductPageSkeleton />;
  }

  return (
    <div className="product-info">
      <div className="image-list-container">
        {product.images.map((image, i) => (
          <Image
            src={image}
            key={image}
            onClick={() => handleImageClick(i)}
            className={classNames({ selected: displayedImageIndex === i })}
          />
        ))}
      </div>
      <FullViewCarousel dots={false} arrows infinite onChange={handleCarouselChange} ref={ref}>
        {product.images.map((image) => (
          <div key={image} className="product-image-container">
            {product.totalLikes > 0 && <TotalSavesTag totalLikes={product.totalLikes} />}
            <Image src={image} />
            {!isShopOwner && <CircleLikeButton isLiked={product.isLiked} onClick={onLikeToggle} />}
          </div>
        ))}
      </FullViewCarousel>
      <ProductDetails
        title={product.title}
        selectedQuantity={selectedQuantity}
        price={product.price}
        originalPrice={product.originalPrice}
        currencyCode={product.currencyCode}
        labels={product.labels}
        isShopOwner={isShopOwner}
        quantity={product.quantity}
        bannerDetails={bannerDetails}
        isDigital={product.isDigital}
        isItemInBasket={isAddedToBasketClicked}
        shippingCountries={shippingCountries}
        recipientCountry={recipientCountry}
        shippingInformation={product.shippingInformation}
        estimatedDeliveryTime={estimatedDeliveryTime}
        vacationDetails={vacationDetails}
        shouldShowPaypalButton={isConnectedToPaypal}
        onChangeCountry={onCountryChange}
        onChangeQuantity={setSelectedQuantity}
        onAddToBasket={onAddToBasket}
        onBuyNow={onBuyNow}
        onEditListing={onEdit}
        onShareListing={onShare}
      />
    </div>
  );
};

const mapStateToProps = (state: IStore2) => {
  const { userId, shopDetails, estimatedDeliveryTime, isLoading, vacationDetails, bannerDetails } =
    state.specificProduct;

  const product = SpecificProductSelector.getProductSelector(state);

  const loggedUserId = UserSelector.loggedUserIdSelector(state);

  const token = UserSelector.tokenSelector(state);

  const isShopOwner = userId === loggedUserId;

  const defaultShippingAddress = SettingsSelector.defaultShippingAddressSelector(state);

  const recipientCountry = defaultShippingAddress?.country || null;

  const { countries: shopCountries, connectedPayouts } = shopDetails || {
    countries: [],
    connectedPayouts: null,
  };

  const shippingCountries = product.shippingInformation
    ? product.shippingInformation.countries
    : shopCountries;

  const isConnectedToPaypal =
    !!connectedPayouts?.includes(PayoutMethods.Paypal) && product.hasInstantCheckout;

  return {
    product,
    isShopOwner,
    vacationDetails,
    estimatedDeliveryTime,
    isConnectedToPaypal,
    recipientCountry,
    bannerDetails,
    shippingCountries,
    isLoading,
    token,
    defaultShippingAddress,
  };
};

const mapDispatchToProps = {
  showPopup: PopupSlicer.actions.show,
  onBuyNowItem: BasketSlicer.actions.updateBuyNowItem,
  updateBasketSource: BasketSlicer.actions.updateSource,
  updateInitialDetails: CheckoutSlicer.actions.updateInitialDetails,
  addItemToBasket: onAddItemToBasket,
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductDetailsWrapper);
